import { IServerData } from "../interfaces/serverInterfaces";
import Service from '../services/service';
import {InstanceHelper} from "~/helpers/instanceHelper";

interface IMembershipResponse {
  membership: any
}

export default class OrganizationProvider extends Service {
  add(organization) {
    return this.persist({ json: organization, method: 'post' });
  }

  save(organization) {
    return this.persist({ endpoint: organization.id, json: organization });
  }

  invite({ organizationId, groupId, memberships, invitation }) {
    const endpoint = groupId ? `${organizationId}/groups/${groupId}/invite` : `${organizationId}/invite`;
    return this.persist({ endpoint: endpoint, method: 'post', memberships: memberships, invitation: invitation });
  }

  removeInvitation({organizationId, invitationId}) {
    const endpoint = `${organizationId}/destroy_invite`;
    return this.persist({ endpoint: endpoint, method: 'delete', invitation: {id: invitationId} });
  }

  resendInvitation({organizationId, invitationId}) {
    const endpoint = `${organizationId}/resend_invite`;
    return this.persist({ endpoint: endpoint, method: 'post', invitation: {id: invitationId} });
  }

  fetchMemberships({ organizationId, groupId }) {
    return this.persist({ endpoint: `${organizationId}/memberships`, method: 'get', groupId: groupId });
  }

  createGroup({ organizationId, group }) {
    return this.persist({ endpoint: `${organizationId}/groups`, method: 'post', group: group });
  }

  updateGroup({ organizationId, group }) {
    return this.persist({ endpoint: `${organizationId}/groups/${group.id}`, group: group });
  }

  convert({ organizationId, group }) {
    return this.persist({ endpoint: `${organizationId}/groups/${group.id}/convert`, method: 'post' });
  }

  unassignApp({ organizationId, group }) {
    return this.persist({ endpoint: `${organizationId}/groups/${group.id}/unassign_app`, method: 'post', group: group });
  }

  fetchGroups({ organizationId }) {
    return this.persist({ endpoint: `${organizationId}/groups`, method: 'get' });
  }

  fetchShare(organizationId, folderId, userId) {
    return this.persist({ endpoint: `${organizationId}/folders/${folderId}/shares/user_share`, method: 'get', userId: userId });
  }

  updateMembership({organizationId, groupId, membership, serialize}) {
    const endpoint = groupId ? `${organizationId}/groups/${groupId}/memberships/${membership.id}` : `${organizationId}/memberships/${membership.id}`;

    return this.persist({endpoint: endpoint, membership: membership, serialize});
  }

  transferOwnership(organizationId, membershipId, transferToId) {
    return this.persist({endpoint: `${organizationId}/memberships/${membershipId}/transfer_owned_resources`, method: 'post', transferToId: transferToId});
  }

  fetchOwnedResources(organizationId, membershipId) {
    return this.persist({endpoint: `${organizationId}/memberships/${membershipId}/owned_resources`, method: 'get'});
  }

  removeMembership({organizationId, groupId, membershipId}) {
    const endpoint = groupId ? `${organizationId}/groups/${groupId}/memberships/${membershipId}` : `${organizationId}/memberships/${membershipId}`;
    return this.persist({ endpoint: endpoint, method: 'delete'});
  }

  destroyGroup({ organizationId, groupId }) {
    return this.persist({ endpoint: `${organizationId}/groups/${groupId}`, method: 'delete'});
  }

  addToGroup({ organizationId, groupId, userId, role_ids, invitationId }): Promise<IServerData<IMembershipResponse>[0]> {
    if (invitationId) { // Pending Member
      return this.persist<IMembershipResponse>({ endpoint: `${organizationId}/groups/${groupId}/add_pending_member/${invitationId}`, method: 'post', role_ids: role_ids });
    } else {
      return this.persist<IMembershipResponse>({ endpoint: `${organizationId}/groups/${groupId}/add_member/${userId}`, method: 'post', role_ids: role_ids });
    }
  }

  addMembers({organizationId, groupId, groupMembers, serialize}) {
    return this.persist({ endpoint: `${organizationId}/groups/${groupId}/add_members`, method: 'post', group_members: groupMembers, serialize })
  }

  fetchGroupApps({ organizationId, groupId }) {
    return this.persist({
      endpoint: `${ organizationId }/groups/${ groupId }/applications`,
      method: 'get',
    });
  }

  createPaymentGateway({ organizationId, paymentGateway }) {
    return this.persist({ endpoint: `${organizationId}/payment_gateways`, method: 'post', paymentGateway: paymentGateway });
  }

  updatePaymentGateway({ organizationId, paymentGateway }) {
    return this.persist({ endpoint: `${organizationId}/payment_gateways/${paymentGateway.id}`, method: 'put', paymentGateway: paymentGateway });
  }

  replacePaymentGateway({ organizationId, paymentGateway, replacementGatewayId }) {
    return this.persist({ endpoint: `${organizationId}/payment_gateways/${paymentGateway.id}/replace`, method: 'post', paymentGateway: paymentGateway.id, replacementGatewayId: replacementGatewayId });
  }

  destroyPaymentGateway({ organizationId, paymentGateway }) {
    return this.persist({ endpoint: `${organizationId}/payment_gateways/${paymentGateway.id}`, method: 'delete' });
  }

  fetchPaymentGateways({ organizationId, }) {
    return this.persist({ endpoint: `${organizationId}/payment_gateways`, method: 'get'});
  }

  createFolder({ organizationId, folder, idTree }) {
    return this.persist({ endpoint: `${organizationId}/folders`, method: 'post', folder: folder, idTree: idTree });
  }

  updateFolder({ organizationId, folder, idTree }) {
    return this.persist({ endpoint: `${organizationId}/folders/${folder.id}`, folder: folder, idTree: idTree });
  }

  destroyFolder({ organizationId, folder, idTree }) {
    return this.persist({ endpoint: `${organizationId}/folders/${folder.id}`, method: 'delete', idTree: idTree });
  }

  moveFolder({ organizationId, folderId, idTree, newParentIdTree }) {
    return this.persist({ endpoint: `${organizationId}/folders/${folderId}/move`, idTree: idTree, newParentIdTree: newParentIdTree });
  }

  createForm({ organizationId, folderIdTree, form }) {
    return this.persist({ endpoint: 'forms', method: 'post', folderIdTree: folderIdTree, form: form });
  }

  fetchDocuments({ folder, query }) {
    return this.persist({ endpoint: `${folder?.organization?.id || folder.owning_organization_id}/folders/${folder.id}/fetch_documents`, method: 'get', idTree: folder.idTree(), query: query });
  }

  fetchFolders({organizationId}) {
    return this.persist({endpoint: `${organizationId}/folders`, method: 'get'});
  }

  downloadFolder({organizationId, folderId, includeDependencies}) {    
    return this.persist({ 
      endpoint: `${organizationId}/folders/${folderId}/download?dependencies=${includeDependencies}`,
      method: 'get'
    });
  }

  fetchPeerOrganizationConnections({organizationId}) {
    return this.persist({
      endpoint: `${organizationId}/peer_organization_connections`,
      method: 'get'
    });
  }

  invitePeerOrganization({organizationId, subdomain}) {
    return this.persist({
      endpoint: `${organizationId}/peer_organization_connections`,
      method: 'post',
      subdomain,
    });
  }

  acceptOrRejectPeerOrganization({id, isAccepted}) {
    return this.persist({
      endpoint: `peer_organization_connections/${id}`,
      method: 'put',
      isAccepted,
    });
  }

  fetchPeerOrganizationProfile({organizationId, peerOrgConnectionId}) {
    return this.persist({
      endpoint: `${organizationId}/peer_organization_connections/${peerOrgConnectionId}/fetch_peer_organization_profile`,
      method: 'get',
    });
  }

  destroyPeerOrganization({id}) {
    return this.persist({
      endpoint: `peer_organization_connections/${id}`,
      method: 'delete',
    });
  }

  peerOrganizationShareDocuments({organizationId, peerOrgConnectionId, documentIds}) {
    return this.persist({
      endpoint: `${organizationId}/peer_organization_connections/${peerOrgConnectionId}/share_documents`,
      method: 'put',
      documentIds
    });
  }

  peerOrganizationUnshareDocument({organizationId, peerOrgConnectionId, documentId}) {
    return this.persist({
      endpoint: `${organizationId}/peer_organization_connections/${peerOrgConnectionId}/unshare_document`,
      method: 'put',
      documentId
    });
  }
  
  persist<T = any>({
            endpoint = '',
            json,
            method = 'put',
            invitation,
            invitationId,
            membership,
            memberships,
            group,
            groupId,
            folder,
            folderId,
            idTree,
            newParentIdTree,
            query,
            role_ids,
            share,
            paymentGateway,
            replacementGatewayId,
            userId,
            transferToId,
            subdomain,
            isAccepted,
            documentIds,
            documentId,
            group_members,
            serialize,
          }: any): Promise<T> {
    return new Promise((resolve, reject) => {
      let data = {
        _method: method,
        authenticity_token: this.csrfToken(),
      };

      if (query) {
        data = window.AppConfig.mergeJson(
            data,
            typeof query.asJson === 'function' ? query.asJson() : query,
        );
      }

      if (json) {
        data['organization'] = json;
      }

      if (invitation) {
        data['invitation'] = invitation;
      }

      if (invitationId) {
        data['invitationId'] = invitationId;
      }

      if (membership) {
        data['membership'] = membership;
      }

      if (memberships) {
        data['memberships'] = memberships;
      }

      if (group) {
        data['group'] = group;
      }

      if (groupId) {
        data['group_id'] = groupId;
      }

      if (folder) {
        data['folder'] = folder;
      }

      if (idTree) {
        data['id_tree'] = idTree;
      }

      if (newParentIdTree) {
        data['new_parent_id_tree'] = newParentIdTree;
      }

      if (role_ids !== undefined) {
        data['role_ids'] = role_ids;
      }

      if (share) {
        data['share'] = share;
      }

      if (paymentGateway) {
        data['payment_gateway'] = paymentGateway;
      }

      if (replacementGatewayId) {
        data['replacement_gateway_id'] = replacementGatewayId;
      }

      if (userId) {
        data['userId'] = userId;
      }

      if (transferToId) {
        data['transfer_to'] = transferToId;
      }

      if (subdomain) {
        data['subdomain'] = subdomain;
      }

      if (InstanceHelper.isBoolean(isAccepted)) {
        data['is_accepted'] = isAccepted;
      }

      if (documentIds) {
        data['document_ids'] = documentIds;
      }

      if (documentId) {
        data['document_id'] = documentId;
      }

      let overrideUrl;
      if (endpoint.startsWith('peer_organization_connections')) {
        overrideUrl = `/${endpoint}`;
      }

      if (group_members) {
        data['group_members'] = group_members;
      }

      if (serialize) {
        data['serialize'] = serialize
      }

      $.ajax({
        url: overrideUrl || `/organizations/${endpoint}`,
        type: 'POST',
        dataType: 'json',
        data: data,
      }).done(function (result) {
        resolve(result);
      }).fail(function (result) {
        reject();
      });
    });
  }
}